import { get, readable } from "svelte/store";

import { point } from "@turf/helpers";
import lineArc from "@turf/line-arc";

import GeoHelper from "../Helpers/GeoHelper";
import VesselProperty from "../Models/Enums/VesselProperty";
import VesselGroupHelper from "../Models/VesselGroupHelper";

import AlertsStore from "./Store";

import DistanceUnit from "../UI/MeasureTool/DistanceUnit";
import DistanceUtil from "../UI/MeasureTool/DistanceUtil";

const AlertsHelpers = {};

// <helpers>
AlertsHelpers.minutesSinceUpdate = readable(0, set => {
	const minuteLength = 60000; // adjustable for debugging
	window.setInterval(_ => {
		const minutesSinceUpdate = (Date.now() - get(AlertsStore.meta.lastUpdated)) / minuteLength;
		const fixedMinute =
			isNaN(minutesSinceUpdate) || minutesSinceUpdate > 999999 || minutesSinceUpdate < 0
				? 0
				: minutesSinceUpdate | 0; // bitwise rounddown with OR
		set(fixedMinute);
	}, 15000); // ^AG: Changed this to every 10 seconds not every single second!
});

AlertsHelpers.isValidVesselFeature = feature => {
	return (
		feature.geometry.coordinates[0] >= -180 &&
		feature.geometry.coordinates[0] <= 180 &&
		feature.geometry.coordinates[1] >= -90 &&
		feature.geometry.coordinates[1] <= 90
	);
};
AlertsHelpers.isAlertTypeVisible = type => {
	return AlertsStore.filters.alertTypes[type].isVisible();
};
AlertsHelpers.isAlertTypeSubcategoryVisible = (type, subcategory) => {
	const subcategories = get(AlertsStore.filters.alertTypes[type].subcategories);
	for (let i = 0; i < subcategories.length; i++) {
		if (subcategories[i].name === subcategory) {
			if (get(subcategories[i].visible)) {
				return true;
			}
		}
	}
	return false;
};

AlertsHelpers.printFilterState = () => {
	const alertTypes = Object.keys(AlertsStore.filters.alertTypes);
	for (let i = 0; i < alertTypes.length; i++) {
		const filterType = AlertsStore.filters.alertTypes[alertTypes[i]];
		console.log(alertTypes[i], get(filterType.visible), get(filterType.exists), "isV:", filterType.isVisible(), filterType.countCategoriesWithName());

		const subcategories = get(filterType.subcategories);
		for (let j = 0; j < subcategories.length; j++) {
			console.log("\t" + subcategories[j].name, get(subcategories[j].visible), get(subcategories[j].exists));
		}
	}
};

/**
 * Takes an IMO, returns a vessel or false if there isn't one
 * @param {int} imo The imo you want the vessel feature of
 */
// @deprecated
// AlertsHelpers.getVesselFromImo = imo => {
// const vesselData = get(AlertsStore.vesselGroup.vessels);
// const filtered = vesselData.filter(vessel => "" + vessel.imo === "" + imo);
// // TODO: build tolerance to imo duplicates
// return filtered.length > 0 ? filtered[0] : false;
// };

// TODO: change references to this to direct reference to store
// AlertsHelpers.getSelectedImos = () => {
// 	return get(AlertsStore.vesselGroup.selected[VesselProperty.IMO]);
// };

// countSelectedAlerts now deprecated
// AlertsHelpers.countSelectedVessels = () => {
// 	const count = AlertsHelpers.getSelectedImos().length;
// 	return count;
// };

// @deprecated
// AlertsHelpers.imoIsInList = imo => {
// 	// const liveVessels = get(AlertsStore.vesselGroup.vessels);
// 	// return (liveVessels.findIndex(vessel => "" + vessel.imo === "" + imo) > -1);
// 	return
// };

// @deprecated
// AlertsHelpers.isVesselIMOSelected = imo => {
// 	const vessel = AlertsHelpers.getVesselFromImo(imo);
// 	if (!vessel) return false;
// 	return vessel.visible;
// };

AlertsHelpers.hasAlertType = (type, vesselList = null) => {
	if (vesselList === null) {
		vesselList = get(AlertsStore.vesselGroup.vessels);
	}
	const res = vesselList.filter(vessel => {
		let anyMatches = false;
		vessel.alertTypes.forEach(vesselAlert => {
			if (vesselAlert === type) anyMatches = true;
		});
		return anyMatches;
	});
	return res.length >= 1;
};

AlertsHelpers.isVesselVisibleForAlertFilters = (vessel) => {
	// We have to check against AlertFilters here.
	// if (get(AlertsStore.filters.vesselType) !== "" && vessel.classification !== get(AlertsStore.filters.vesselType)) {
	// 	return false;
	// }
	// if (get(AlertsStore.filters.flag) !== "" && vessel.flag !== get(AlertsStore.filters.flag)) {
	// 	return false;
	// }
	// if (get(AlertsStore.filters.destination) !== "" && vessel.voyageDestination !== get(AlertsStore.filters.destination)) {
	// 	return false;
	// }
	if (AlertsStore.filters.advanced.length > 0) {
		return AlertsStore.filters.advanced.apply([vessel]).length === 1;
	}
	return true;
};

/**
 * Downsamples as one dataset, for use with one MMSI/IMO at a time
 */
AlertsHelpers.downsampleHistoricData = (historicFeatureArray, DOWNSAMPLE_RATE = 3000000) => {
	historicFeatureArray.sort((a, b) => {
		a = Date.parse(a.properties.time_stamp);
		b = Date.parse(b.properties.time_stamp);
		return a > b;
	});
	const returnData = [];
	let lastTimeStamp = new Date(0);
	historicFeatureArray.forEach(feature => {
		const nextTimeStamp = Date.parse(feature.properties.time_stamp);
		const timeBetweenEntries = nextTimeStamp - lastTimeStamp;
		if (timeBetweenEntries > DOWNSAMPLE_RATE) {
			returnData.push(feature);
			lastTimeStamp = nextTimeStamp;
			lastTimeStamp = Date.parse(feature.properties.time_stamp);
		}
	});

	return returnData;
};

// TODO: If this is used a lot, cache it and recalculate on update/dirty
// -- would only be used a lot if we're updating data very frequently
AlertsHelpers.GetGeoJson = {
	vessels: {
		all: () => {
			const vessels = get(AlertsStore.vesselGroup.vessels);
			const allLiveFeatures = [];
			vessels.forEach(vessel => allLiveFeatures.push(vessel.feature));
			return GeoHelper.emptyGeoJson(allLiveFeatures);
		},
		// live data as filtered by visibility and selection
		filtered: () => {
			const anySelected = AlertsStore.vesselGroup.countSelected() > 0; // AlertsStore.helpers.countSelectedVessels() > 0;
			const filteredFeatures = [];
			get(AlertsStore.vesselGroup.vessels).forEach(vessel => {
				if ((!anySelected || vessel.selected) && vessel.visible) {
					filteredFeatures.push(vessel.feature);
				}
			});
			return GeoHelper.emptyGeoJson(filteredFeatures);
		},
		// As displayed on map i.e. snapshot adjusted + visible
		display: () => {
			const displaySnapshotFeatures = [];
			get(AlertsStore.vesselGroup.vessels).forEach(vessel => {
				if (vessel.visible) {
					if (!AlertsHelpers.isVesselVisibleForAlertFilters(vessel)) {
						return;
					}
					// If there is no feature, it will be set to false
					// if (vessel.snapshotFeature) displaySnapshotFeatures.push(vessel.snapshotFeature);
					if (vessel.currentFeature) displaySnapshotFeatures.push(vessel.currentFeature);
				}
			});
			return GeoHelper.emptyGeoJson(displaySnapshotFeatures);
		}
	},
	paths: {
		all: () => {
			return VesselGroupHelper.toPathGeometry(get(AlertsStore.vesselGroup.vessels));
		},
		// As displayed on map - if any selected, only show selection, plus visibility property
		// (same as filtred so property skipped)
		display: () => {
			const filter = (vessel) => {
				return vessel.selected && AlertsHelpers.isVesselVisibleForAlertFilters(vessel);
			};
			const pointAttach = (vessel, point) => {
				point.properties[VesselProperty.IMO] = vessel.imo;
				point.properties[VesselProperty.NAME] = vessel.name;
				return point;
			};
			return VesselGroupHelper.toPathGeometry(get(AlertsStore.vesselGroup.vessels), filter, pointAttach);
		}
	},
	nearbyBounds: {
		display: () => {
			const circles = [];
			get(AlertsStore.vesselGroup.vessels).forEach(vessel => {
				if (!vessel.isNearbyAlert()) {
					return;
				}
				const nearbyData = vessel.getNearbyAlertData();
				if (nearbyData.label !== "customer") {
					return;
				}

				if (!vessel.visible) {
					return;
				}
				if (!AlertsHelpers.isVesselVisibleForAlertFilters(vessel)) {
					return;
				}

				const radius = nearbyData.shape.radius;
				const radiusUnit = nearbyData.shape.unit;
				const radiusMiles = DistanceUtil.convert(radius, DistanceUnit.NAUTICAL_MILES, DistanceUnit.MILES);
				const center = point([vessel.lng, vessel.lat]);
				const arc = lineArc(center, radiusMiles, 0, 360, { steps: 90, units: "miles" });
				circles.push(arc);
			});
			return GeoHelper.emptyGeoJson(circles);
		}
	}
};
// end AlertsStore.helpers

export default AlertsHelpers;
